<template>
  <div class="screen home">
    <!-- Home is where stars land: the counter walks them up one by one, whether
         they were won in a minigame (store.starsToCelebrate, earned while this
         bar was off screen) or straight off the reel ("Extra sterren", which
         changes the balance with the bar already mounted). -->
    <StatusBar
      :week="store.weekNumber"
      :weeks-total="store.weeksTotal"
      :stars="shownStars"
      action-label="Inwisselen"
      animate-stars
      :pending-stars="store.starsToCelebrate"
      @action="goShop"
      @celebrated="store.celebrationShown()"
    />

    <!-- PLAY MODE: reel + spin. Stay mounted while a spin is animating so the
         final turn's spin plays out before the game-over view takes over. -->
    <div
      v-if="hasTurns || spinning"
      class="home__play"
      :style="{ '--reel-scale': reelScale, '--burst-centre': reelCentre + 'px' }"
    >
      <!-- the sunburst is an <img> so its convergence point can be pinned to the
           centre of the reel; see the note in the stylesheet -->
      <img :src="burstImg" class="home__burst" alt="" aria-hidden="true" />

      <button class="home__help" type="button" aria-label="Uitleg" @click="goUitleg">
        <svg class="home__help-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 17H12.01"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div ref="reelWrap" class="home__reel-wrap">
        <div class="home__stage">
          <SlotReel ref="reel" :items="reelItems" @landed="onLanded" />
          <div class="home__turns" aria-live="polite">
            <strong>{{ store.turnsRemaining }}/{{ store.turnsTotal }}</strong>
            <span>spellen</span>
          </div>
        </div>
      </div>

      <!-- §4.2/4.3 — the segment the reel landed on comes forward out of the drum
           over a blurred screen, breathes while it is up, and leaves after 3s.
           Anchored on --burst-centre, which IS the drum's centre, so it grows
           out of the reel rather than out of nowhere. -->
      <transition name="reveal">
        <div v-if="reveal" class="home__reveal" aria-live="polite">
          <img :src="reveal.image" class="home__reveal-art" :alt="reveal.label" />
        </div>
      </transition>

      <transition name="flash">
        <div v-if="flashMsg" class="home__flash">{{ flashMsg }}</div>
      </transition>

      <div class="screen__footer">
        <div class="home__cta">
          <AppButton variant="primary" :disabled="spinning" @click="spin">
            Draai aan het rad
          </AppButton>
        </div>
      </div>
    </div>

    <!-- GAME OVER / uitgespeeld (§9) — Figma 421:1207 -->
    <div v-else class="home__gameover" :style="{ '--burst-centre': goCentre + 'px' }">
      <!-- same story as play mode: the sunburst is an <img> so its convergence
           point can be pinned to the centre of the "Game Over" sign -->
      <img :src="burstImg" class="home__burst" alt="" aria-hidden="true" />

      <div ref="goBody" class="home__gameover-body" @scroll="fitGameOver">
        <div class="home__gameover-content">
          <img ref="goSign" :src="gameOverImg" class="home__gameover-sign" alt="Game over" />
          <h2 class="home__gameover-title">Je hebt je spellen voor deze week gespeeld</h2>
          <p class="home__gameover-copy">
            Volgende week krijg je weer nieuwe spellen. Zet je sterren nu in op je favoriete
            prijzenpot(ten).
          </p>
        </div>
      </div>
      <div class="screen__footer">
        <AppButton variant="cta" @click="goShop">
          Sterren inwisselen
          <span class="home__stars-chip">
            <StarIcon :size="16" :filled="false" />
            <span>{{ store.starBalance }}</span>
          </span>
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import StatusBar from '@/components/game/StatusBar.vue'
import SlotReel from '@/components/game/SlotReel.vue'
import AppButton from '@/components/ui/AppButton.vue'
import StarIcon from '@/components/ui/StarIcon.vue'
import { MINIGAMES } from '@/config/gameConfig'
import { gameLoader } from '@/games/registry'
// Reel segment art (Figma Home frame 426:294). Only wordle still uses the design's
// "?" placeholder tile — its real game art isn't finalised yet.
import reelRocket from '@/assets/v3/reel-rocket.webp'
import reelSoccer from '@/assets/v3/reel-soccer.webp'
import reelStar from '@/assets/v3/reel-star.webp'
import reelExtraSpel from '@/assets/v3/reel-extra-spel.webp'
import reelQuestion from '@/assets/v3/reel-question.webp'
import reelExtraSterren from '@/assets/v3/reel-extra-sterren.webp'
import burstImg from '@/assets/v3/sunburst-bg.webp'
import gameOverImg from '@/assets/v3/gameover-sign.webp'

const store = useGameStore()
const router = useRouter()

const reel = ref(null)
const spinning = ref(false)
const flashMsg = ref('')
// The reel segment that is currently held up over the screen (extra spel /
// extra sterren), and the balance the top bar keeps showing until it leaves.
const reveal = ref(null)
const heldStars = ref(null)
const shownStars = computed(() => heldStars.value ?? store.starBalance)

// How long the segment stays up before the reel is free again (§4.2/4.3).
const REVEAL_MS = 3000

// How long the landed segment stays lit before the game takes over. Matches
// SlotReel's own celebrate window, so the glow doesn't get cut off mid-pulse.
const CELEBRATE_MS = 1900

const hasTurns = computed(() => store.hasTurns)

// --- Reel fit ---------------------------------------------------------------
// The reel is centred in the space between the status bar and the CTA, and
// scaled to fill it. The scale has to be a plain NUMBER (transform: scale()),
// and CSS cannot divide a length down to a number, so it is measured here
// instead of being approximated with dvh arithmetic — which also means it
// reacts to the real box, keyboard, safe areas and all.
const REEL_BLOCK_H = 422 // neon sign overhang (50) + drum (372)
const REEL_BLOCK_HALF_W = 153.5 // drum half (104.5) + the "spellen" badge sticking out (49)
const REEL_SIGN_OVER = 50 // how far the sign hangs above the drum
const REEL_SCALE = [0.55, 1.1] // never smaller / bigger than this
const REEL_MARGIN_Y = 16
const REEL_MARGIN_X = 24

const reelWrap = ref(null)
const reelScale = ref(1)
const reelCentre = ref(280) // px from the panel's top (replaced on mount)

function fitReel() {
  const el = reelWrap.value
  if (!el) return
  const { width, height } = el.getBoundingClientRect()
  if (!width || !height) return
  const s = Math.min(
    (height - REEL_MARGIN_Y * 2) / REEL_BLOCK_H,
    (width / 2 - REEL_MARGIN_X) / REEL_BLOCK_HALF_W,
    REEL_SCALE[1],
  )
  reelScale.value = Math.max(REEL_SCALE[0], s)
  // The wrap pads its top by the sign's overhang so that centring the drum's
  // box centres the WHOLE block; the drum's centre then lands here. The
  // sunburst is hung on this same point.
  reelCentre.value = (height + REEL_SIGN_OVER * reelScale.value) / 2
}

let ro = null
onMounted(() => {
  fitReel()
  if (typeof ResizeObserver === 'function') {
    ro = new ResizeObserver(fitReel)
    if (reelWrap.value) ro.observe(reelWrap.value)
  }
})
onBeforeUnmount(() => ro?.disconnect())
// the reel only exists while there are turns left — re-observe when it returns
watch(
  () => hasTurns.value || spinning.value,
  async (on) => {
    if (!on) return
    await nextTick()
    fitReel()
    if (ro && reelWrap.value) ro.observe(reelWrap.value)
  },
)

// --- Game-over fit ----------------------------------------------------------
// Exactly the same idea as the reel: the sunburst hangs by its own convergence
// point, so that point has to follow whatever the layout does with the neon
// "Game Over" sign. Measured rather than derived, because the sign's centre
// depends on the wrapped title/copy above and below it.
const goBody = ref(null)
const goSign = ref(null)
const goCentre = ref(230)

function fitGameOver() {
  const panel = goBody.value?.parentElement
  const sign = goSign.value
  if (!panel || !sign) return
  const p = panel.getBoundingClientRect()
  const s = sign.getBoundingClientRect()
  if (!p.height || !s.height) return
  goCentre.value = s.top + s.height / 2 - p.top
}

let goRo = null
watch(
  () => !(hasTurns.value || spinning.value),
  async (on) => {
    goRo?.disconnect()
    goRo = null
    if (!on) return
    await nextTick()
    fitGameOver()
    if (typeof ResizeObserver !== 'function') return
    // the panel resizes with the viewport; the sign resizes again when it decodes
    goRo = new ResizeObserver(fitGameOver)
    if (goBody.value) goRo.observe(goBody.value)
    if (goSign.value) goRo.observe(goSign.value)
  },
  { immediate: true },
)
onBeforeUnmount(() => goRo?.disconnect())

// Reel segments — real Figma art per outcome, with the design's exact segment
// colour and art size (px within the 175-wide segment; Figma Home 426:294).
// `icon` (emoji) is only a fallback if an image ever fails to load.
const SEGMENT = {
  // sizes are the exact Figma art-box widths (426:300–304) within the 175px segment
  extra_sterren: {
    color: 'var(--c-seg-cream)',
    image: reelExtraSterren,
    size: 94,
    label: 'Extra sterren',
    icon: '⭐',
  },
  match3: { color: 'var(--c-red-deep)', image: reelStar, size: 94, icon: '⭐' },
  timing: { color: 'var(--c-seg-navy)', image: reelRocket, size: 82, icon: '🚀' },
  unblock: { color: 'var(--c-seg-olive)', image: reelSoccer, size: 71, icon: '⚽' },
  wordle: { color: 'var(--c-orange-bright)', image: reelQuestion, size: 84, icon: '🔤' },
  extra_spel: {
    color: 'var(--c-seg-cream)',
    image: reelExtraSpel,
    size: 94,
    label: '+1 Extra spel',
    icon: '➕',
  },
}
// Reel cycle ordered so that at rest the payline sits between the rocket and the
// soccer (Figma hero). Each segment now has its own art (match3 = star, wordle = "?",
// extra_sterren = "+2 Extra Sterren" badge), so none look alike when they land.
const REEL_ORDER = ['timing', 'unblock', 'extra_spel', 'wordle', 'extra_sterren', 'match3']
const reelItems = REEL_ORDER.map((key) => ({
  key,
  ...SEGMENT[key],
  label: SEGMENT[key].label ?? MINIGAMES.find((g) => g.id === key)?.name ?? key,
}))

onMounted(() => {
  if (store.activeGame) router.replace({ name: 'game' }) // §6 resume
})

async function spin() {
  if (spinning.value) return
  spinning.value = true
  flashMsg.value = ''
  // Freeze the count: the server answers (and the balance lands) long before
  // the reel stops, and the stars must not be counted up before the player has
  // even seen what they rolled.
  heldStars.value = store.starBalance
  // Start the reel on the click and run the server call in parallel — the reel
  // free-spins until the outcome arrives, so the click feels instant instead of
  // waiting on the network. landOn() enforces a minimum spin time.
  reel.value?.startSpin()
  const res = await store.spin()
  if (res.error) {
    reel.value?.stopSpin() // ease the reel to a stop, no landing
    flash(res.error === 'no_turns' ? 'Geen spellen meer deze week' : 'Er ging iets mis')
    heldStars.value = null
    spinning.value = false
    return
  }
  const outcome = store.lastSpinOutcome
  const key = outcome.type === 'minigame' ? outcome.gameId : outcome.type
  // guard: the reel can be gone if the view changed during the await
  if (!reel.value) {
    heldStars.value = null
    spinning.value = false
    return
  }
  reel.value.landOn(key)
}

function onLanded() {
  const outcome = store.lastSpinOutcome
  if (outcome.type === 'minigame') {
    // §4: let the landing celebrate (the segment lights up + the arrows pulse)
    // play out, then open the game. It is deliberately unhurried — this is the
    // moment the player finds out which game they got. Its OWN uitleg screen
    // rises up from the bottom (GameView's "rise" transition); there is no
    // separate "we beginnen zo…" card. Warm the game chunk during the celebrate
    // so its uitleg is ready to slide straight in.
    gameLoader(outcome.gameId)?.()
    setTimeout(() => router.push({ name: 'game' }), CELEBRATE_MS)
    return
  }

  // Extra spel / extra sterren: bring the segment forward, hold it, then let go.
  // The stars are only released to the counter once the segment is on its way
  // out — otherwise they would be counted up behind a screen the player is still
  // reading.
  const seg = SEGMENT[outcome.type]
  reveal.value = { image: seg.image, label: seg.label }
  setTimeout(() => {
    reveal.value = null
    heldStars.value = null
    spinning.value = false
  }, REVEAL_MS)
}

function flash(msg) {
  flashMsg.value = msg
  setTimeout(() => (flashMsg.value = ''), 2600)
}

function goShop() {
  router.push({ name: 'shop' })
}
function goUitleg() {
  router.push({ name: 'uitleg' })
}
</script>

<style lang="scss" scoped>
.home {
  // Bound the column to the viewport (.screen only sets a min-height) so the reel
  // has a fixed box to be fitted into and the CTA can never be pushed off-screen:
  // the reel gives way (it scales) instead of the page growing.
  height: 100vh;
  height: 100dvh;
  // White backing so the rounded top corners of the content panel read against white
  background: var(--c-white);
  color: var(--c-on-brand);
}

// The status bar is the top element now that the branded header is gone;
// keep only the notch/safe-area clearance so its content sits at the very top.
.home :deep(.status-bar) {
  padding-top: max(env(safe-area-inset-top), var(--sp-3));
}

// Orange sunburst content panel — rounded top corners below the white status bar
// (Figma 426:286 → border-radius: 45px 45px 0 0).
.home__play,
.home__gameover {
  background: var(--c-red);
  border-radius: 45px 45px 0 0;
}
.home__play {
  // Both set from fitReel() in the script; these are only the pre-measure
  // fallbacks. --burst-centre is where the drum's centre ends up, measured from
  // the top of this panel; the sunburst is hung on that same point, because
  // `background-size: cover` can only pin its convergence to a fixed FRACTION
  // of the box (same story as the intro's key visual).
  --reel-scale: 1;
  --burst-centre: 280px;

  position: relative;
  overflow: hidden; // clip the oversized sunburst to the rounded top corners
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

// Sized like `cover` used to be, but scaled up just enough that it still reaches
// every edge once its convergence point is pulled onto the anchor (A) — the
// reel's centre in play mode, the neon sign's centre on game over.
// With 40.95% of the image above that point, the image height H must satisfy:
//   top edge    <= 0      ->  H >= A / 0.4095
//   bottom edge >= bottom ->  H >= (height - A) / 0.5905   (100dvh is a safe bound)
//   never narrower than the column (what `cover` guaranteed)
//                         ->  H >= column / 0.566314 = 1.7658 * column
.home__burst {
  position: absolute;
  left: 50%; // both anchors are centred in the column
  top: var(--burst-centre);
  width: auto;
  max-width: none;
  height: calc(
    1.015 *
      max(
        var(--burst-centre) / 0.4095,
        (100dvh - var(--burst-centre)) / 0.5905,
        1.7658 * min(100vw, var(--app-max-width))
      )
  );
  // put the image's convergence point (49.4% / 40.95%) on the anchor
  transform: translate(-49.4%, -40.95%);
  pointer-events: none;
}

.home__help {
  position: absolute;
  top: 30px;
  right: 24px;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 47px;
  height: 40px;
  color: var(--c-ink);
  background: var(--c-white);
  border: none;
  border-radius: 17px; // Figma 426:310
  box-shadow: var(--sh-pill);
}

.home__help-icon {
  display: block;
  width: 24px;
  height: 24px;
}

// The reel is centred in whatever is left between the status bar and the CTA.
// The padding is the neon sign's overhang above the drum, so centring the DRUM's
// box here actually centres the sign+drum block as a whole.
.home__reel-wrap {
  position: relative; // above the sunburst
  flex: 1 1 auto;
  min-height: 0; // shrink instead of pushing the CTA away — the reel scales to fit
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-top: calc(50px * var(--reel-scale));
}

// Figma CTA sits ~56px above the bottom edge on the 771 frame
.home .screen__footer {
  position: relative; // above the sunburst
  flex: none;
  padding-bottom: max(44px, env(safe-area-inset-bottom));
}

// The reel's internals are fixed px (SlotReel's strip maths depends on them), so
// it is fitted with a transform — which also keeps it out of the layout, so the
// scale can never feed back into the measurement that produced it.
.home__stage {
  position: relative;
  width: 209px;
  transform: scale(var(--reel-scale));
  transform-origin: center center;
}

.home__turns {
  position: absolute;
  top: 43px;
  right: -49px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 111px;
  height: 58px; // Figma 426:288
  // the left ~half of the badge hides behind the drum frame, so the text is
  // shifted into the visible right part (Figma text centred at x315, not x292)
  padding-left: 50px;
  color: var(--c-ink);
  background: var(--c-white);
  border-radius: 16px;
  box-shadow: var(--sh-float);
  line-height: 1.05;
  // sits BEHIND the gold drum frame (Figma 426:288 renders before the drum), so the
  // bulbs overlap its left edge and it emerges to the right over the sunburst.
  z-index: 0;

  strong {
    font-size: var(--fs-lg);
  }
  span {
    font-size: var(--fs-xs);
  }
}

.home__flash {
  position: relative; // above the sunburst
  margin: var(--sp-3) var(--sp-5) 0;
  padding: var(--sp-3) var(--sp-4);
  text-align: center;
  font-weight: var(--fw-bold);
  color: var(--c-ink);
  background: var(--c-white);
  border-radius: var(--r-md);
  box-shadow: var(--sh-card);
}

// white backing plate behind the 3D blue CTA (Figma 299×77 over 288×57)
// §4.2/4.3 reveal — the landed segment comes forward out of the drum over the
// blurred screen. Same scrim + blur as the shell's own modals (game/shop overlay),
// so the "something is being shown to you" language stays one language.
.home__reveal {
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(6px);
  pointer-events: none; // it leaves by itself; nothing to click
}

// Anchored on the drum's centre, so it grows out of the reel and not out of the
// middle of the panel. `breathe` keeps running the whole time it is up.
.home__reveal-art {
  position: absolute;
  left: 50%;
  top: var(--burst-centre);
  width: min(232px, 62vw);
  height: auto;
  transform: translate(-50%, -50%);
  animation: reveal-breathe 2.1s var(--ease-in-out) infinite;
  filter: drop-shadow(0 14px 30px rgba(0, 0, 0, 0.45));
}

@keyframes reveal-breathe {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.06);
  }
}

// It starts at roughly the size it had in the segment and swells to full size;
// the scrim fades in underneath at the same time.
.reveal-enter-active {
  transition: opacity 0.42s var(--ease-out);
}
.reveal-leave-active {
  transition: opacity 0.34s var(--ease-out);
}
.reveal-enter-from,
.reveal-leave-to {
  opacity: 0;
}
.reveal-enter-active .home__reveal-art {
  animation: reveal-in 0.62s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.reveal-leave-active .home__reveal-art {
  animation: reveal-out 0.34s var(--ease-out) both;
}

@keyframes reveal-in {
  from {
    transform: translate(-50%, -50%) scale(0.42);
  }
  70% {
    transform: translate(-50%, -50%) scale(1.05);
  }
  to {
    transform: translate(-50%, -50%) scale(1);
  }
}
@keyframes reveal-out {
  to {
    transform: translate(-50%, -50%) scale(0.86);
  }
}

.home__cta {
  width: 299px;
  max-width: 100%;
  margin: 0 auto;
  padding: 5px 5px 15px;
  background: var(--c-white);
  border-radius: 22px;
  // A beat every 3s to keep drawing the eye back to the one thing to do here.
  // It runs on the plate, not the button: the button owns its own `transform`
  // for the 3D press, and two animations on one property would fight.
  animation: cta-beat 3s var(--ease-in-out) infinite;
}
// nothing to invite while the reel is already going
.home__play:has(.home__reveal) .home__cta,
.home__cta:has(:disabled) {
  animation: none;
}

@keyframes cta-beat {
  0%,
  86%,
  100% {
    transform: scale(1);
  }
  91% {
    transform: scale(1.035);
  }
  95.5% {
    transform: scale(0.994);
  }
}

// Game over — Figma 421:1207: neon "Game Over" sign (298×239), title, copy, and
// a flat CTA carrying the star balance. Same rule as the other screens: the CTA
// sits at the bottom, the rest is centred in the space left over.
.home__gameover {
  // measured in fitGameOver(); this is only the pre-measure fallback
  --burst-centre: 230px;

  position: relative;
  overflow: hidden; // clip the oversized sunburst to the rounded top corners
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.home__gameover-body {
  position: relative; // above the sunburst
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  // last resort on a viewport too short even for the shrunken sign: scroll
  // rather than run the copy into the CTA below
  overflow-y: auto;
  // symmetric, so it guarantees a gap above the CTA without moving the centred
  // group off the design's position
  padding: var(--sp-4) var(--sp-5);
  text-align: center;
}

// `margin-block: auto` rather than `justify-content: center`, so overflow spills
// downwards (scrollable) instead of being cut off at the top. `min-height: 0`
// lets it shrink below its content height — which is what hands the shortage
// down to the sign.
.home__gameover-content {
  flex: 0 1 auto;
  min-height: 0;
  margin-block: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.home__gameover-sign {
  width: min(298px, 85%); // Figma 421:1251 — 298 wide on the 390 artboard
  height: auto;
  // The sign is the only thing here that may give way: on a short screen it is
  // the flex item that shrinks (the text keeps its height), and `contain` keeps
  // its aspect while it does — so it just renders smaller, still centred, and
  // the copy never reaches the CTA.
  flex: 0 1 auto;
  min-height: 0;
  object-fit: contain;
  margin-bottom: clamp(24px, 11.15dvh, 86px); // Figma — sign 380 → title 466
}

.home__gameover-title,
.home__gameover-copy {
  flex: none;
}

.home__gameover-title {
  max-width: 275px; // Figma 421:1223
  margin: 0;
  font-size: var(--fs-2xl);
  font-weight: var(--fw-bold);
  line-height: 1.2;
}

.home__gameover-copy {
  max-width: 286px; // Figma 421:1224
  margin: var(--sp-4) 0 0;
  font-size: var(--fs-lg);
  line-height: 1.28;
  color: var(--c-white);
}

// Figma 421:1228 — white pill on the CTA showing the star balance
.home__stars-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 8px;
  font-size: var(--fs-base);
  font-weight: var(--fw-bold);
  color: var(--c-black);
  background: var(--c-white);
  border-radius: 36px;
  box-shadow: 0 3px 0 rgba(0, 0, 0, 0.25);
}

.home__gameover :deep(.btn--cta) {
  // Figma 421:1226 — 342×69, 23px bold label, 18px radius. The label eases off
  // on a narrow column so it keeps sharing one line with the star chip.
  height: 69px;
  font-size: clamp(18px, 5.9vw, var(--fs-2xl));
  border-radius: 18px;
}
.home__gameover :deep(.btn__label) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 11px; // Figma — label ends 263, chip starts 274
}
.home__gameover .screen__footer {
  // Figma — CTA 342 wide at x=24, bottom edge 53px off the frame
  padding: 0 24px max(53px, env(safe-area-inset-bottom));
}

.flash-enter-active,
.flash-leave-active {
  transition:
    opacity var(--dur-base),
    transform var(--dur-base);
}
.flash-enter-from,
.flash-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
